/** @jsxImportSource @emotion/react */
import { useTranslation } from 'next-i18next'
import { FormClose, FormSearch } from 'grommet-icons'
import { Box, Keyboard, Text, TextInput } from 'grommet'
import { ChangeEvent, useCallback, useMemo, useState } from 'react'

import { trackEvent } from 'utils/useGTM'
import useDeviceSize from 'hooks/useDevice'
import { BasicEmotion, Emotion } from 'db-tables'
import { INITIAL_DIAL_ANGLE } from 'hooks/useMoodPlot'
import EmotionName from 'components/emotion/EmotionName'
import { findEmotionCentralAngle } from 'util/averageMood'

const formatSuggestions = (emotions: (Emotion | BasicEmotion)[], small: boolean) => {
  return emotions.map((emotion) => {
    return {
      label: (
        <Box pad={small ? 'large' : 'xsmall'} key={emotion.id}>
          <Text size={small ? 'large' : 'small'}>
            <EmotionName emotion={emotion} />
          </Text>
        </Box>
      ),
      value: emotion,
    }
  })
}

type Props = {
  emotions: (Emotion | BasicEmotion)[]
  onSelected: (emotion: Emotion | BasicEmotion, angle: number) => void
}

export default function EmotionsAutocomplete({ emotions, onSelected }: Props) {
  const { t } = useTranslation()
  const { small } = useDeviceSize()

  const defaultEmotions = useMemo(
    // Somewhat randomized sorting, but mostly positive emotions are listed
    () => emotions.sort((e) => e.angle_start - e.angle_end + INITIAL_DIAL_ANGLE),
    [emotions],
  )

  const [input, setInput] = useState('')
  const clearSelection = useCallback(() => setInput(''), [])

  const [suggestions, setSuggestions] = useState(defaultEmotions)
  const suggestionOptions = useMemo(
    () => formatSuggestions(suggestions, small),
    [small, suggestions],
  )

  const onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const nextValue = event.target.value
      setInput(nextValue)
      if (nextValue) {
        const regexp = new RegExp(`^${nextValue}`, 'i')
        // Filter by the chosen language
        const nextSuggestions = emotions.filter((emotion) =>
          regexp.test(t(`emotions:${emotion.name}.name`)),
        )
        setSuggestions(nextSuggestions)
      }
    },
    [emotions, t],
  )

  const onSuggestionSelect = useCallback(
    (event: { suggestion: { value: Emotion | BasicEmotion } }) => {
      const emotion = event.suggestion.value
      const newAngle = findEmotionCentralAngle(emotion.angle_start, emotion.angle_end)
      trackEvent({ event: 'AutocompleteWordPick', emotion: emotion.name })
      onSelected(emotion, newAngle)
      setInput('')
    },
    [onSelected],
  )

  return (
    <Keyboard onEsc={clearSelection}>
      <TextInput
        reverse
        value={input}
        size="xsmall"
        dropHeight="medium"
        icon={
          input ? (
            <FormClose pointerEvents="auto" cursor="pointer" onClick={clearSelection} />
          ) : (
            <FormSearch />
          )
        }
        onChange={onChange}
        suggestions={suggestionOptions}
        onSuggestionSelect={onSuggestionSelect}
        placeholder={
          <Text size={small ? 'small' : 'xsmall'}>
            {t('components.emotions-autocomplete.search-for-emotion')}
          </Text>
        }
      />
    </Keyboard>
  )
}
